import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetCallRecordByIdQuery,
  useUpdateCallRecordMutation,
  useLazyGetLeadsQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { getErrorMessage } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { CALL_RECORDS_LIST_PATH } from '../../navigations/path';
import { buildLeadsAutocompleteParams } from '../../components/Leads/utils';
import { normalizeLeadOption, loadCallRecordStatuses } from './callRecordFormMeta';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import CallRecordingUploadSection from './CallRecordingUploadSection';
import {
  uploadRecordingAttachmentFileChunked,
  extractAttachmentIdFromUploadResponse,
} from './callRecordAttachmentUpload';
import { getRecordingsList } from './callRecordHelpers';


const EditCallRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updateCallRecord, { isLoading: submitting }] = useUpdateCallRecordMutation();
  const [fetchLeadsTrigger] = useLazyGetLeadsQuery();

  const callRecordStatusTrigger = useCallback(
    () => ({
      unwrap: () => loadCallRecordStatuses(),
    }),
    []
  );

  const statusSelect = useLazySelectOptions(callRecordStatusTrigger, {
    mapResult: (res) => (Array.isArray(res) ? res : []),
  });
  const statusOptions = statusSelect.options;

  const [leadInput, setLeadInput] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadsAutocompleteOpen, setLeadsAutocompleteOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusId, setStatusId] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [replacementAttachmentId, setReplacementAttachmentId] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [existingRecordings, setExistingRecordings] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors({ recordings_0: 'recordings' });

  const {
    data: callRecordData,
    isLoading: recordLoading,
    isError: recordError,
    error: recordErr,
  } = useGetCallRecordByIdQuery(
    {
      id,
      // `closer` is not used by this form
      params: { 'relations[]': ['lead', 'status', 'recordings'] },
    },
    { skip: !id }
  );

  const fetchLeads = useCallback(
    async (searchTerm) => {
      setLeadLoading(true);
      try {
        const params = buildLeadsAutocompleteParams({
          search: searchTerm,
          page: 1,
          perPage: 100,
        });
        const res = await fetchLeadsTrigger(params).unwrap();
        const raw = extractList(res);
        const list = (Array.isArray(raw) ? raw : []).map(normalizeLeadOption).filter(Boolean);
        setLeadOptions(list);
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to load leads');
        setLeadOptions([]);
      } finally {
        setLeadLoading(false);
      }
    },
    [fetchLeadsTrigger]
  );

  useEffect(() => {
    if (!leadsAutocompleteOpen) return;
    const t = setTimeout(() => fetchLeads(leadInput), 350);
    return () => clearTimeout(t);
  }, [leadInput, fetchLeads, leadsAutocompleteOpen]);

  const handleLeadsAutocompleteOpen = () => {
    setLeadsAutocompleteOpen(true);
    void fetchLeads(leadInput);
  };

  const handleStatusSelectOpen = () => {
    statusSelect.load().catch((err) => {
      toast.error(err?.message || 'Failed to load statuses');
    });
  };

  useEffect(() => {
    if (recordError) {
      toast.error(recordErr?.data?.message || 'Failed to load recording');
      navigate(CALL_RECORDS_LIST_PATH);
    }
  }, [recordError, recordErr, navigate]);

  useEffect(() => {
    if (!callRecordData || hydrated) return;
    const item = callRecordData?.data ?? callRecordData ?? {};
    setExistingRecordings(getRecordingsList(item));
    if (item.lead) {
      const nl = normalizeLeadOption(item.lead);
      setSelectedLead(nl);
      setLeadOptions(nl ? [nl] : []);
    }
    const sid = item.status?.id ?? item.status_id ?? item.status?.uuid ?? '';
    setStatusId(sid != null && sid !== '' ? String(sid) : '');
    setNote(item.note ?? '');
    setHydrated(true);
  }, [callRecordData, hydrated]);

  const handleReplacementFile = async (nextFile) => {
    if (!nextFile) {
      setFile(null);
      setReplacementAttachmentId(null);
      return;
    }
    setUploadingAttachment(true);
    setFile(nextFile);
    setReplacementAttachmentId(null);
    try {
      const uploadBody = await uploadRecordingAttachmentFileChunked(nextFile, { modelId: id });
      const attachmentId = extractAttachmentIdFromUploadResponse(uploadBody);
      if (!attachmentId) {
        toast.error('Attachment upload succeeded but no id was returned.');
        setFile(null);
        return;
      }
      setReplacementAttachmentId(String(attachmentId));
      toast.success('New recording uploaded — click Save to update.');
    } catch (err) {
      toast.error(getErrorMessage(err) || err.response?.data?.message || 'Attachment upload failed');
      setFile(null);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveReplacement = () => {
    setFile(null);
    setReplacementAttachmentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;

    const clientErrors = {};
    if (!leadKey) clientErrors.lead_id = 'Select a lead';
    if (!statusId) clientErrors.status_id = 'Select a status';
    if (file && !replacementAttachmentId)
      clientErrors.recordings = 'Wait for the attachment upload to finish, or remove the file.';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;
    if (uploadingAttachment) return;

    try {
      if (file && replacementAttachmentId) {
        const fd = new FormData();
        fd.append('lead_id', String(leadKey));
        fd.append('status_id', String(statusId));
        fd.append('note', note || '');
        fd.append('recordings[0]', String(replacementAttachmentId));
        await updateCallRecord({ id, data: fd }).unwrap();
      } else {
        await updateCallRecord({
          id,
          data: {
            lead_id: leadKey,
            status_id: statusId,
            note: note || '',
          },
        }).unwrap();
      }
      toast.success('Recording updated.');
      navigate(CALL_RECORDS_LIST_PATH);
    } catch (err) {
      applyApiError(err, 'Failed to update recording');
    }
  };

  if (recordLoading && !hydrated) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3, display: 'flex', justifyContent: 'center', minHeight: '40vh' }}>
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Edit recording
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={leadOptions}
                  loading={leadLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => opt?.label || opt?.name || opt?.email || ''}
                  isOptionEqualToValue={(a, b) => (a?.id ?? a?.uuid) === (b?.id ?? b?.uuid)}
                  value={selectedLead}
                  onChange={(_, v) => {
                    setSelectedLead(v);
                    clearError('lead_id');
                  }}
                  onInputChange={(_, v) => setLeadInput(v)}
                  onOpen={handleLeadsAutocompleteOpen}
                  renderInput={(params) => (
                    <TextField {...params} label="Lead" required {...fieldProps('lead_id')} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!errors.status_id}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusId}
                    onChange={(e) => {
                      setStatusId(e.target.value);
                      clearError('status_id');
                    }}
                    onOpen={handleStatusSelectOpen}
                    disabled={statusSelect.loading && !statusOptions.length}
                  >
                    {statusOptions.map((s) => {
                      const val = s.id ?? s.uuid;
                      return (
                        <MenuItem key={String(val)} value={String(val)}>
                          {s.name ?? s.title ?? s.label ?? String(val)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.status_id && (
                    <FormHelperText>{errors.status_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  rows={3}
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    clearError('note');
                  }}
                  {...fieldProps('note')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {errors.recordings && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {errors.recordings}
          </Typography>
        )}

        <CallRecordingUploadSection
          file={file}
          attachmentId={replacementAttachmentId}
          existingRecordings={existingRecordings}
          uploadingAttachment={uploadingAttachment}
          saving={submitting}
          onFileChange={(f) => {
            void handleReplacementFile(f);
          }}
          onRemove={handleRemoveReplacement}
          disabled={submitting}
          optionalReplace
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          <Button
            type="button"
            variant="contained"
            disabled={submitting || uploadingAttachment || (Boolean(file) && !replacementAttachmentId)}
            onClick={handleSubmit}
          >
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Cancel
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default EditCallRecord;
